import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import {
  ChevronDown,
  ArrowUpRight,
  X,
  Layers,
  Truck,
  Sun,
  MapPin,
  GitCommitVertical,
  ShieldAlert,
  Gauge,
  PaintBucket,
  Globe,
} from "lucide-react"
import "./SiteNavbar.css"

const navLinks = [
  { label: "Home", href: "/", hasDropdown: false },
  { label: "About", href: "/about", hasDropdown: false },
  { label: "Products", href: "#products", hasDropdown: true, key: "products" },
  { label: "Brands", href: "#brands", hasDropdown: true, key: "brands" },
]

const products = [
  {
    slug: "avery-dennison-reflective-sheeting",
    icon: Layers,
    title: "Avery Dennison Reflective Sheeting",
    image: "/images/products/reflective-sheeting.jpg",
  },
  {
    slug: "reflective-conspicuity-tape",
    icon: Truck,
    title: "Reflective Conspicuity Tape",
    image: "/images/products/conspicuity-tape.jpg",
  },
  {
    slug: "solar-cats-eyes",
    icon: Sun,
    title: "Solar Cat's Eyes",
    image: "/images/products/solar-cats-eyes.jpg",
  },
  {
    slug: "rayolite-marker-road-studs",
    icon: MapPin,
    title: "Rayolite Marker Road Studs",
    image: "/images/products/rayolite-marker.jpg",
  },
  {
    slug: "flexible-delineator-posts",
    icon: GitCommitVertical,
    title: "Flexible Delineator Posts",
    image: "/images/products/delineator-posts.jpg",
  },
  {
    slug: "sma-crash-cushions",
    icon: ShieldAlert,
    title: "SMA Crash Cushions",
    image: "/images/products/sma-crash-cushion.jpg",
  },
  {
    slug: "scorpion-truck-mounted-attenuators",
    icon: ShieldAlert,
    title: "Scorpion Truck-Mounted Attenuators",
    image: "/images/products/scorpion-attenuator3.jpg",
  },
  {
    slug: "delta-grx-retroreflectometer",
    icon: Gauge,
    title: "Delta GRX Retroreflectometer",
    image: "/images/products/delta-grx.jpg",
  },
  {
    slug: "ltl-3500-retroreflectometer",
    icon: PaintBucket,
    title: "LTL 3500 Paint Retroreflectometer",
    image: "/images/products/ltl-3500.jpg",
  },
]

const brands = [
  { name: "Avery Dennison", src: "/images/logos/Avery-Dennison.png" },
  { name: "TrafFix Devices Inc.", src: "/images/logos/traffix-logo.jpg" },
  { name: "Ver-Mac", src: "/images/logos/ver-mac-logo-hi-signs-trafficsupply-trafficrentals.jpg" },
  { name: "Delta (Force Technology)", src: "/images/logos/DeltaLogo-big.jpg" },
  { name: "Rayolite", src: "/images/logos/Rayolite_274pix.jpg" },
  { name: "SMA", src: "/images/logos/sma-logo.png" },
  { name: "Midsign", src: "/images/logos/midsign-logo.png" },
]

// Custom hamburger icon: top + middle lines full width, bottom line shorter
function HamburgerIcon({ size = 26 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
      <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
      <line x1="3" y1="18" x2="14" y2="18" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
    </svg>
  )
}

export function SiteNavbar() {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [openDesktopMenu, setOpenDesktopMenu] = useState(null) // "products" | "brands" | null
  const [openMobileMenu, setOpenMobileMenu] = useState(null) // "products" | "brands" | null
  const navRef = useRef(null)

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [open])

  // Close desktop dropdown when clicking outside an open dropdown
  useEffect(() => {
    function handleClickOutside(e) {
      if (!e.target.closest(".navbar__dropdown-wrap")) {
        setOpenDesktopMenu(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  function toggleDesktopMenu(key) {
    setOpenDesktopMenu((prev) => (prev === key ? null : key))
  }

  function toggleMobileMenu(key) {
    setOpenMobileMenu((prev) => (prev === key ? null : key))
  }

  function toggleLang() {
    i18n.changeLanguage(i18n.resolvedLanguage === "ar" ? "en" : "ar")
  }

  function closeAll() {
    setOpen(false)
    setOpenDesktopMenu(null)
    setOpenMobileMenu(null)
  }

  return (
    <header className="navbar">
      <nav className="navbar__inner" ref={navRef}>
        {/* Logo */}
        <a href="/" className="navbar__logo">
          <span className="navbar__logo-badge">
            <img src="/images/roads-reflectors-logo.webp" alt="Road's Reflectors company logo" />
          </span>
          <span className="navbar__logo-text">
            <span className="navbar__logo-title">
              Road&apos;s <span>Reflectors</span>
            </span>
            <span className="navbar__logo-sub">{t("Road Safety Solutions")}</span>
          </span>
        </a>

        {/* Center nav pill */}
        <div className="navbar__pill">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <div key={link.label} className="navbar__dropdown-wrap">
                <button
                  type="button"
                  className={`navbar__link navbar__link--btn ${
                    openDesktopMenu === link.key ? "navbar__link--active" : ""
                  }`}
                  onClick={() => toggleDesktopMenu(link.key)}
                >
                  {t(link.label)}
                  <ChevronDown
                    size={14}
                    className={openDesktopMenu === link.key ? "navbar__chevron--open" : ""}
                  />
                </button>

                {link.key === "products" && (
                  <div
                    className={`navbar__dropdown navbar__dropdown--products ${
                      openDesktopMenu === "products" ? "navbar__dropdown--open" : ""
                    }`}
                  >
                    {products.map((item) => {
                      const Icon = item.icon
                      return (
                        <a
                          key={item.slug}
                          href={`/products/${item.slug}`}
                          className="navbar__dropdown-item"
                          onClick={() => setOpenDesktopMenu(null)}
                        >
                          <span className="navbar__dropdown-icon">
                            <Icon size={16} />
                          </span>
                          {t(item.title)}
                        </a>
                      )
                    })}
                  </div>
                )}

                {link.key === "brands" && (
                  <div
                    className={`navbar__dropdown navbar__dropdown--brands ${
                      openDesktopMenu === "brands" ? "navbar__dropdown--open" : ""
                    }`}
                  >
                    {brands.map((brand) => (
                      <div key={brand.name} className="navbar__dropdown-brand">
                        <span className="navbar__dropdown-brand-logo">
                          <img src={brand.src} alt={brand.name} />
                        </span>
                        <span className="navbar__dropdown-brand-name">{brand.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <a key={link.label} href={link.href} className="navbar__link">
                {t(link.label)}
              </a>
            )
          )}
        </div>

        {/* CTA */}
        <div className="navbar__actions">
         <button
            type="button"
            onClick={toggleLang}
           aria-label={t("Change language")}
            className="navbar__lang"
          >
            <Globe size={15} />
           {i18n.resolvedLanguage === "ar" ? "English" : "العربية"}
         </button>
          <Link to="/contact" className="btn-pill btn-pill--brand navbar__cta">
           {t("Contact Us")}
            <span className="btn-pill__arrow">
              <ArrowUpRight size={14} />
            </span>
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={t("Toggle menu")}
            className="navbar__toggle"
          >
            {open ? <X size={24} /> : <HamburgerIcon size={40} />}
          </button>
        </div>
      </nav>

      {/* Mobile menu — full-width slide-in drawer */}
      <div
        className={`navbar__backdrop ${open ? "navbar__backdrop--open" : ""}`}
        onClick={closeAll}
        aria-hidden="true"
      />
      <div className={`navbar__mobile ${open ? "navbar__mobile--open" : ""}`}>
        <div className="navbar__mobile-header">
          <a href="/" className="navbar__mobile-logo" onClick={closeAll}>
            <span className="navbar__logo-badge navbar__logo-badge--mobile">
              <img src="/images/roads-reflectors-logo.webp" alt="Road's Reflectors company logo" />
            </span>
            <span className="navbar__logo-text">
              <span className="navbar__logo-title">
                Road&apos;s <span>Reflectors</span>
              </span>
            </span>
          </a>
          <button
            type="button"
            onClick={closeAll}
            aria-label="Close menu"
            className="navbar__mobile-close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="navbar__mobile-links">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <div key={link.label} className="navbar__mobile-group">
                <button
                  type="button"
                  className="navbar__mobile-link navbar__mobile-link--btn"
                  onClick={() => toggleMobileMenu(link.key)}
                >
                  {t(link.label)}
                  <ChevronDown
                    size={16}
                    className={openMobileMenu === link.key ? "navbar__chevron--open" : ""}
                  />
                </button>

                {link.key === "products" && (
                  <div
                    className={`navbar__mobile-submenu ${
                      openMobileMenu === "products" ? "navbar__mobile-submenu--open" : ""
                    }`}
                  >
                    {products.map((item) => {
                      const Icon = item.icon
                      return (
                        <a
                          key={item.slug}
                          href={`/products/${item.slug}`}
                          className="navbar__mobile-sublink"
                          onClick={closeAll}
                        >
                          <Icon size={15} />
                          {t(item.title)}
                        </a>
                      )
                    })}
                  </div>
                )}

                {link.key === "brands" && (
                  <div
                    className={`navbar__mobile-submenu ${
                      openMobileMenu === "brands" ? "navbar__mobile-submenu--open" : ""
                    }`}
                  >
                    {brands.map((brand) => (
                      <div
                        key={brand.name}
                        className="navbar__mobile-sublink navbar__mobile-sublink--brand"
                      >
                        <span className="navbar__dropdown-brand-logo navbar__dropdown-brand-logo--mobile">
                          <img src={brand.src} alt={brand.name} />
                        </span>
                        {brand.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className="navbar__mobile-link"
                onClick={closeAll}
              >
                {t(link.label)}
              </a>
            )
          )}
        </div>
        <button
          type="button"
          onClick={toggleLang}
          aria-label={t("Change language")}
          className="navbar__lang navbar__lang--mobile"
          style={{ marginTop: "1.25rem", width: "100%", justifyContent: "center" }}
        >
          <Globe size={15} />
          {i18n.resolvedLanguage === "ar" ? "English" : "العربية"}
        </button>
        <Link
          to="/contact"
          className="btn-pill btn-pill--brand"
          onClick={closeAll}
          style={{ marginTop: "0.75rem", justifyContent: "center", width: "100%" }}
        >
          {t("Contact Us")}
          <ArrowUpRight size={16} />
        </Link>
      </div>
    </header>
  )
}